import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  TextField,
  Alert,
  Paper,
  Chip,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAutomationCatalog, useAutomationRules } from "../../hooks/useAutomationRules";
import { useUsers } from "../../hooks/useUsers";
import { useChannels } from "../../hooks/useChannels";
import { useTypes } from "../../hooks/useTypes";
import { usePaymentMethods } from "../../hooks/usePaymentMethods";
import { useDeliveryMethods } from "../../hooks/useDeliveryMethods";
import { usePositions } from "../../hooks/usePositions";
import { useAgents } from "../../hooks/useAgents";
import { fetchData } from "../../api/client";
import { getUserFacingApiMessage } from "../../utils/getUserFacingApiMessage";
import {
  applyReferenceMappings,
  buildAutomationRuleCreatePayload,
  collectFlowReferences,
  findChatbotAutomationEventId,
  flattenImportItems,
  groupImportItemsByStep,
  REF_KIND_AGENT,
  REF_KIND_AUTOMATION,
  REF_KIND_CHANNEL,
  REF_KIND_DELIVERY,
  REF_KIND_PAYMENT,
  REF_KIND_POSITION,
  REF_KIND_SUBTYPE,
  REF_KIND_TYPE,
  REF_KIND_USER,
  validateImportMappings,
} from "../../utils/chatbotFlowReferences";

const KIND_LABEL_KEYS = {
  [REF_KIND_AUTOMATION]: "chatbotConfigPage.flowImportWizardKindAutomation",
  [REF_KIND_USER]: "chatbotConfigPage.flowImportWizardKindUser",
  [REF_KIND_AGENT]: "chatbotConfigPage.flowImportWizardKindAgent",
  [REF_KIND_CHANNEL]: "chatbotConfigPage.flowImportWizardKindChannel",
  [REF_KIND_TYPE]: "chatbotConfigPage.flowImportWizardKindType",
  [REF_KIND_SUBTYPE]: "chatbotConfigPage.flowImportWizardKindSubtype",
  [REF_KIND_PAYMENT]: "chatbotConfigPage.flowImportWizardKindPayment",
  [REF_KIND_DELIVERY]: "chatbotConfigPage.flowImportWizardKindDelivery",
  [REF_KIND_POSITION]: "chatbotConfigPage.flowImportWizardKindPosition",
};

function mapBasic(rows, labelKey = "nome") {
  return (rows || []).map((r) => ({
    value: Number(r?.id ?? r?.value),
    label: String(r?.[labelKey] ?? r?.nome ?? r?.name ?? r?.descricao ?? r?.id ?? ""),
  }));
}

function findSuggestedOption(options, label) {
  if (!label || !options?.length) return null;
  const norm = String(label).trim().toLowerCase();
  return (
    options.find((o) => String(o.label).trim().toLowerCase() === norm) ||
    options.find((o) => String(o.label).trim().toLowerCase().includes(norm)) ||
    null
  );
}

function ImportRefRow({
  item,
  mode,
  onModeChange,
  selectedValue,
  onSelectValue,
  options,
  loading,
  t,
}) {
  const kindLabel = t(KIND_LABEL_KEYS[item.kind] || "chatbotConfigPage.flowImportWizardKindOther");

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Box>
          <Chip size="small" label={kindLabel} sx={{ mb: 0.75 }} />
          <Typography variant="body2" fontWeight={600}>
            {item.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("chatbotConfigPage.flowImportWizardSourceId", { id: item.sourceId })}
          </Typography>
        </Box>

        {item.allowCreate ? (
          <RadioGroup
            row
            value={mode}
            onChange={(e) => onModeChange(e.target.value)}
          >
            <FormControlLabel
              value="create"
              control={<Radio size="small" />}
              label={t("chatbotConfigPage.flowImportWizardModeCreate")}
            />
            <FormControlLabel
              value="map"
              control={<Radio size="small" />}
              label={t("chatbotConfigPage.flowImportWizardModeMap")}
            />
          </RadioGroup>
        ) : null}

        {mode === "create" && item.allowCreate ? (
          <Alert severity="info" sx={{ py: 0.5 }}>
            {t("chatbotConfigPage.flowImportWizardCreateHint")}
          </Alert>
        ) : (
          <>
            {!item.allowCreate && item.kind === REF_KIND_AUTOMATION ? (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                {t("chatbotConfigPage.flowImportWizardCreateUnavailableHint")}
              </Alert>
            ) : null}
            <Autocomplete
              size="small"
              options={options}
              loading={loading}
              value={options.find((o) => String(o.value) === String(selectedValue)) || null}
              onChange={(_, opt) => onSelectValue(opt?.value ?? "")}
              getOptionLabel={(o) => o?.label || ""}
              isOptionEqualToValue={(a, b) => String(a?.value) === String(b?.value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required={item.required || mode === "map"}
                  label={
                    item.required
                      ? t("chatbotConfigPage.flowImportWizardSelectRequired")
                      : t("chatbotConfigPage.flowImportWizardSelectOptional")
                  }
                  placeholder={t("chatbotConfigPage.flowImportWizardSelectPlaceholder")}
                />
              )}
            />
          </>
        )}
      </Stack>
    </Paper>
  );
}

export default function ChatbotFlowImportWizard({
  open,
  flowDoc,
  parameterForm,
  sourceLabel,
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation("settings");
  const [activeStep, setActiveStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [modes, setModes] = useState({});
  const [selections, setSelections] = useState({});

  const { data: automationMeta } = useAutomationCatalog();
  const { data: automationRulesData, updateMutation } = useAutomationRules(1, 500, "");
  const { data: usersData, isLoading: loadingUsers } = useUsers(1, 1000, "", "todos");
  const { data: channelsData, isLoading: loadingChannels } = useChannels(1, 1000, "");
  const { data: typesData, isLoading: loadingTypes } = useTypes(1, 1000, "");
  const { data: payData, isLoading: loadingPays } = usePaymentMethods(1, 1000, "");
  const { data: deliveryData, isLoading: loadingDelivery } = useDeliveryMethods(1, 1000, "");
  const { data: positionsData, isLoading: loadingPositions } = usePositions(1, 1000, "");
  const { data: agentsData, isLoading: loadingAgents } = useAgents(1, 200, "");

  const importItems = useMemo(() => {
    const localRefs = collectFlowReferences({ flowDoc, parameterForm });
    return flattenImportItems(localRefs, flowDoc?.refs || {});
  }, [flowDoc, parameterForm]);

  const stepGroups = useMemo(() => {
    const groups = groupImportItemsByStep(importItems);
    return [{ id: "intro", items: [], titleKey: "chatbotConfigPage.flowImportWizardStepIntro" }, ...groups, { id: "confirm", items: [], titleKey: "chatbotConfigPage.flowImportWizardStepConfirm" }];
  }, [importItems]);

  const chatbotRules = useMemo(() => {
    const chatbotEventId = findChatbotAutomationEventId(automationMeta);
    return (automationRulesData?.automationRules || []).filter(
      (r) => Number(r.id_condicao) === Number(chatbotEventId)
    );
  }, [automationRulesData, automationMeta]);

  const optionSets = useMemo(
    () => ({
      [REF_KIND_AUTOMATION]: mapBasic(chatbotRules, "descricao"),
      [REF_KIND_USER]: mapBasic(usersData?.users, "nome"),
      [REF_KIND_AGENT]: mapBasic(agentsData?.agents, "name"),
      [REF_KIND_CHANNEL]: mapBasic(channelsData?.channels),
      [REF_KIND_TYPE]: mapBasic(typesData?.types),
      [REF_KIND_SUBTYPE]: mapBasic(typesData?.types),
      [REF_KIND_PAYMENT]: mapBasic(payData?.paymentMethods),
      [REF_KIND_DELIVERY]: mapBasic(deliveryData?.deliveryMethods),
      [REF_KIND_POSITION]: mapBasic(positionsData?.positions),
    }),
    [chatbotRules, usersData, agentsData, channelsData, typesData, payData, deliveryData, positionsData]
  );

  const loadingSets = {
    [REF_KIND_USER]: loadingUsers,
    [REF_KIND_AGENT]: loadingAgents,
    [REF_KIND_CHANNEL]: loadingChannels,
    [REF_KIND_TYPE]: loadingTypes,
    [REF_KIND_SUBTYPE]: loadingTypes,
    [REF_KIND_PAYMENT]: loadingPays,
    [REF_KIND_DELIVERY]: loadingDelivery,
    [REF_KIND_POSITION]: loadingPositions,
  };

  // Reset apenas ao abrir/trocar o fluxo — NÃO quando optionSets muda (hooks async /
  // invalidate após criar regras). Incluir optionSets aqui reiniciava o stepper (loop).
  React.useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    setBusy(false);
    setError("");
    const nextModes = {};
    for (const item of importItems) {
      nextModes[item.key] = item.allowCreate ? "create" : "map";
    }
    setModes(nextModes);
    setSelections({});
  }, [open, importItems]);

  // Sugestões de mapeamento podem chegar depois (catalogos async) sem resetar o passo.
  React.useEffect(() => {
    if (!open) return;
    setSelections((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const item of importItems) {
        if (next[item.key] != null && next[item.key] !== "") continue;
        const suggested = findSuggestedOption(optionSets[item.kind] || [], item.label);
        if (suggested) {
          next[item.key] = suggested.value;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [open, importItems, optionSets]);

  const currentGroup = stepGroups[activeStep];
  const isIntro = currentGroup?.id === "intro";
  const isConfirm = currentGroup?.id === "confirm";

  const buildIdMap = async () => {
    const idMap = {};
    const chatbotEventId = findChatbotAutomationEventId(automationMeta);

    for (const item of importItems) {
      const mode = modes[item.key] || (item.allowCreate ? "create" : "map");

      if (item.kind === REF_KIND_AUTOMATION && mode === "create") {
        if (!item.allowCreate) {
          throw new Error(t("chatbotConfigPage.flowImportWizardErrorRuleExport"));
        }
        const exportRule = flowDoc?.refs?.[REF_KIND_AUTOMATION]?.[String(item.sourceId)];
        const payload = buildAutomationRuleCreatePayload(exportRule, { chatbotEventId });
        if (!payload) {
          throw new Error(t("chatbotConfigPage.flowImportWizardErrorRuleExport"));
        }
        const res = await updateMutation.mutateAsync(payload);
        const newId = res?.data?.id ?? res?.id;
        if (!newId) throw new Error(t("chatbotConfigPage.flowImportWizardErrorRuleCreate"));
        idMap[item.key] = Number(newId);
        continue;
      }

      const selected = selections[item.key];
      if (selected != null && selected !== "") {
        idMap[item.key] = item.kind === REF_KIND_AGENT ? String(selected) : Number(selected);
        continue;
      }

      if (item.required) {
        throw new Error(t("chatbotConfigPage.flowImportWizardErrorMissingRequired", { label: item.label }));
      }
    }

    return idMap;
  };

  const handleNext = () => {
    setError("");
    if (isIntro) {
      setActiveStep((s) => s + 1);
      return;
    }

    const group = stepGroups[activeStep];
    if (group?.items?.length) {
      for (const item of group.items) {
        const mode = modes[item.key] || (item.allowCreate ? "create" : "map");
        if (item.required || mode === "map") {
          if (mode !== "create" && (selections[item.key] == null || selections[item.key] === "")) {
            setError(t("chatbotConfigPage.flowImportWizardErrorMissingRequired", { label: item.label }));
            return;
          }
        }
      }
    }

    setActiveStep((s) => Math.min(s + 1, stepGroups.length - 1));
  };

  const handleImport = async () => {
    setBusy(true);
    setError("");
    try {
      const idMap = await buildIdMap();
      const missing = validateImportMappings(importItems, idMap);
      if (missing.length) {
        throw new Error(t("chatbotConfigPage.flowImportWizardErrorMissingRequired", { label: missing[0].label }));
      }
      const mappedFlow = applyReferenceMappings(flowDoc, idMap, { parameterForm });
      await onConfirm?.(mappedFlow, { idMap });
    } catch (err) {
      const facing = getUserFacingApiMessage(err);
      setError(facing?.message || err?.message || t("chatbotConfigPage.flowImportError"));
    } finally {
      setBusy(false);
    }
  };

  if (!open || !flowDoc) return null;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("chatbotConfigPage.flowImportWizardTitle")}</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
          {stepGroups.map((g) => (
            <Step key={g.id}>
              <StepLabel>{t(g.titleKey)}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {isIntro ? (
          <Stack spacing={1.5}>
            <Typography variant="body2">
              {t("chatbotConfigPage.flowImportWizardIntro", {
                source: sourceLabel || "JSON",
                name: flowDoc.chatbotName || t("chatbotConfigPage.flowImportWizardUnknownBot"),
              })}
            </Typography>
            <Alert severity="warning">{t("chatbotConfigPage.flowImportHelpReplaceWarning")}</Alert>
            {importItems.length === 0 ? (
              <Alert severity="success">{t("chatbotConfigPage.flowImportWizardNoRefs")}</Alert>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t("chatbotConfigPage.flowImportWizardRefsSummary", { count: importItems.length })}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                  {importItems.slice(0, 8).map((item) => (
                    <Chip key={item.key} size="small" label={item.label} />
                  ))}
                  {importItems.length > 8 ? (
                    <Chip size="small" variant="outlined" label={`+${importItems.length - 8}`} />
                  ) : null}
                </Box>
              </Box>
            )}
          </Stack>
        ) : null}

        {!isIntro && !isConfirm && currentGroup?.items?.length ? (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              {t("chatbotConfigPage.flowImportWizardStepHelp")}
            </Typography>
            {currentGroup.items.map((item) => (
              <ImportRefRow
                key={item.key}
                item={item}
                mode={modes[item.key] || (item.allowCreate ? "create" : "map")}
                onModeChange={(mode) => setModes((prev) => ({ ...prev, [item.key]: mode }))}
                selectedValue={selections[item.key]}
                onSelectValue={(value) => setSelections((prev) => ({ ...prev, [item.key]: value }))}
                options={optionSets[item.kind] || []}
                loading={loadingSets[item.kind]}
                t={t}
              />
            ))}
          </Stack>
        ) : null}

        {isConfirm ? (
          <Stack spacing={1.5}>
            <Typography variant="body2">
              {t("chatbotConfigPage.flowImportConfirmBody", { source: sourceLabel || "JSON" })}
            </Typography>
            <Divider />
            <Typography variant="caption" color="text.secondary">
              {t("chatbotConfigPage.flowImportWizardConfirmDetail", {
                steps: flowDoc.slots?.length || 0,
                refs: importItems.length,
              })}
            </Typography>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("shared.cancel")}
        </Button>
        {!isConfirm ? (
          <Button variant="contained" onClick={handleNext}>
            {t("chatbotConfigPage.flowImportWizardNext")}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleImport} disabled={busy}>
            {busy ? <CircularProgress size={20} /> : t("chatbotConfigPage.flowImportConfirmAction")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export async function fetchAutomationRuleForExport(ruleId) {
  const res = await fetchData(`/automation-rules/${ruleId}`, "GET");
  return res?.data ?? res;
}

export async function fetchEntityForExport(kind, id, fetchers) {
  const fn = fetchers?.[kind];
  if (!fn) return null;
  return fn(id);
}
